import { CoachCard } from '../concept/coach-card';

export interface ActivityRow {
  label: string;
  value: number;
  /** Absent when nothing in the data model backs this row. */
  available: boolean;
}

/**
 * The reference deck's "Scanning senaste 30 dagar".
 *
 * Its four rows are new scans, follow-ups, reminders and targets reached. Two
 * of those count events, and there is no event or reminder model in the API —
 * only a SessionCompletedEvent webhook payload that is never stored. They are
 * listed rather than dropped, marked as not yet tracked, because the gap is a
 * decision for the technical workshop (the deck's own slide 11) and hiding it
 * would lose the question. A fabricated 17 would be worse than an honest dash.
 *
 * Bars are proportional to the largest available row, not to a fixed ceiling,
 * so a quiet month does not render as four empty tracks.
 */
export const ActivityPanel: React.FC<{
  title: string;
  rows: ActivityRow[];
  footer?: React.ReactNode;
}> = ({ title, rows, footer }) => {
  const max = Math.max(1, ...rows.filter((r) => r.available).map((r) => r.value));

  return (
    <CoachCard testId="activity-panel" title={title}>
      <div className="flex flex-col gap-3">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center gap-3">
            <span
              className={`w-[104px] shrink-0 text-xs ${
                r.available ? 'text-foreground-soft' : 'text-muted-foreground'
              }`}
            >
              {r.label}
            </span>
            <span className="h-1.5 min-w-0 flex-1 rounded-sm bg-divider">
              {r.available && (
                <span
                  className="block h-1.5 rounded-sm bg-primary"
                  style={{ width: `${(r.value / max) * 100}%` }}
                />
              )}
            </span>
            <span
              className={`tabular w-8 shrink-0 text-right text-xs font-semibold ${
                r.available ? '' : 'text-muted-foreground'
              }`}
            >
              {r.available ? r.value : '—'}
            </span>
          </div>
        ))}
      </div>
      {rows.some((r) => !r.available) && (
        <p className="text-[10.5px] leading-relaxed text-muted-foreground">
          Follow-ups and reminders are not tracked yet — there is no event model
          behind them. Shown so the gap stays visible rather than guessed at.
        </p>
      )}
      {footer}
    </CoachCard>
  );
};
